import { useState, useEffect } from "react";
import { apiFetch, rawRequest } from "../../lib/api";
import Button from "../../components/Button";
import Card from "../../components/Card";
import type { OfficeRoute } from "./OfficeHome";

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function QboExport({
  onNavigate: _onNavigate,
}: {
  onNavigate: (_route: OfficeRoute) => void;
}) {
  // Default range: First day of current month to today
  const now = new Date();
  const firstOfMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

  const [dateFrom, setDateFrom] = useState(toDateString(firstOfMonth));
  const [dateTo, setDateTo] = useState(toDateString(today));

  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [lastExported, setLastExported] = useState<string | null>(null);

  // Fetch preview count whenever dates change
  useEffect(() => {
    if (!dateFrom || !dateTo) {
      setPreviewCount(null);
      return;
    }

    if (dateTo < dateFrom) {
      setPreviewError("End date must be on or after start date.");
      setPreviewCount(null);
      return;
    }

    setPreviewError(null);
    setPreviewLoading(true);

    apiFetch<{ total: number }>(
      `/api/v1/jobs?state=REVIEWED&dateFrom=${dateFrom}&dateTo=${dateTo}&limit=1`,
    )
      .then((res) => {
        setPreviewCount(res.total);
        setPreviewLoading(false);
      })
      .catch((err) => {
        setPreviewError(err instanceof Error ? err.message : String(err));
        setPreviewLoading(false);
      });
  }, [dateFrom, dateTo]);

  const handleDownload = async () => {
    setDownloadError(null);
    if (!dateFrom || !dateTo) return;

    if (dateTo < dateFrom) {
      setDownloadError("End date must be on or after start date.");
      return;
    }

    setDownloading(true);
    try {
      const res = await rawRequest(
        `/api/v1/jobs/export.csv?dateFrom=${dateFrom}&dateTo=${dateTo}`,
      );
      if (!res.ok) {
        let msg = `Export failed with status ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string; details?: unknown };
          if (body?.error) msg = JSON.stringify(body);
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eagle-dispatch-export-${dateFrom}-to-${dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const nowStr = new Date().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
      setLastExported(nowStr);
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <h1>QuickBooks Online (QBO) Export</h1>
        <p className="sub">Export finalized REVIEWED jobs to accounting CSV</p>
      </header>

      <div style={{ maxWidth: "600px", margin: "0 auto" }}>
        <Card>
          <h2>Export Range</h2>
          <p className="muted" style={{ fontSize: "14px", marginBottom: "16px" }}>
            Select the finish date window for reviewed invoices.
          </p>

          <div className="date-picker-grid">
            <div className="form-group">
              <label htmlFor="exportFrom">From Date</label>
              <input
                id="exportFrom"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="exportTo">To Date</label>
              <input
                id="exportTo"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          {previewError && (
            <div className="banner banner-danger" style={{ marginTop: "12px" }}>
              {previewError}
            </div>
          )}

          <div className="export-preview-box">
            <div className="preview-stat">
              <span className="stat-label">Matching Jobs:</span>
              <span className="stat-value">
                {previewLoading ? "Calculating…" : previewCount !== null ? previewCount : "—"}
              </span>
            </div>
            <p className="muted" style={{ fontSize: "13px", marginTop: "6px" }}>
              Only jobs in <strong>REVIEWED</strong> state within the finish date range are included.
            </p>
          </div>

          {downloadError && (
            <div className="banner banner-danger" style={{ marginTop: "16px" }}>
              {downloadError}
            </div>
          )}

          <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "16px" }}>
            <Button
              onClick={handleDownload}
              disabled={downloading || previewLoading || previewCount === 0 || !!previewError}
            >
              {downloading ? "Generating CSV…" : "Download QBO CSV"}
            </Button>
            {lastExported && (
              <span className="muted" style={{ fontSize: "13px" }}>
                Last exported: {lastExported}
              </span>
            )}
          </div>

          {previewCount === 0 && !previewLoading && !previewError && (
            <p className="muted" style={{ marginTop: "12px", fontSize: "13px" }}>
              No REVIEWED jobs found in this date range.
            </p>
          )}
        </Card>
      </div>
    </main>
  );
}
